import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import DatePicker from '@/components/ui/DatePicker';
import Icon from '@/components/AppIcon';
import Select from '@/components/ui/Select';
import LoadingState from '@/components/ui/LoadingState';
import { inviteEmployee, generateEmployeeId } from '@/lib/api/employeeInvitations';
import useLockBodyScroll from '@/hooks/useLockBodyScroll';
import { useLocations } from '@/context/LocationsContext';
import useTranslation from '@/hooks/useTranslation';

const DEFAULT_FORM = {
  email: '',
  invitedName: '',
  employeeId: '',
  role: '',
  location: '',
  startDate: '',
  message: '',
  department: '',
};

const ROLE_OPTIONS = [
  { value: 'General Manager', department: 'Management', translationKey: 'generalManager' },
  { value: 'Assistant Manager', department: 'Management', translationKey: 'assistantManager' },
  { value: 'Head Chef', department: 'Kitchen', translationKey: 'headChef' },
  { value: 'Sous Chef', department: 'Kitchen', translationKey: 'sousChef' },
  { value: 'Line Cook', department: 'Kitchen', translationKey: 'lineCook' },
  { value: 'Pastry Chef', department: 'Kitchen', translationKey: 'pastryChef' },
  { value: 'Bartender', department: 'Bar', translationKey: 'bartender' },
  { value: 'Server', department: 'Service', translationKey: 'server' },
  { value: 'Host', department: 'Service', translationKey: 'host' },
  { value: 'Barista', department: 'Service', translationKey: 'barista' },
  { value: 'Dishwasher', department: 'Kitchen', translationKey: 'dishwasher' },
  { value: 'Delivery Driver', department: 'Logistics', translationKey: 'deliveryDriver' },
];

const InviteEmployeeModal = ({ onClose, onInvitationCreated, defaultLocation = null }) => {
  useLockBodyScroll();

  const { t } = useTranslation(['employeeDirectory']);
  const [formValues, setFormValues] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [isOpening, setIsOpening] = useState(true);
  const {
    locations,
    loading: locationsLoading,
    error: locationsError,
    refetch: refetchLocations,
  } = useLocations();
  const defaultLocationName = useMemo(
    () => defaultLocation?.name?.trim() ?? '',
    [defaultLocation],
  );
  const hasAppliedDefaultLocation = useRef(false);

  const roleOptions = useMemo(
    () =>
      ROLE_OPTIONS.map((option) => ({
        ...option,
        label: t(
          `employeeDirectory:inviteModal.roleOptions.${option.translationKey}.label`,
          option.value,
        ),
      })),
    [t],
  );

  const branchOptions = useMemo(() => {
    if (!Array.isArray(locations)) {
      return [];
    }
    return locations
      .filter((location) => location?.name)
      .map((location) => ({
        value: location.name,
        label: location.name,
        description: location.address || undefined,
      }));
  }, [locations]);

  const hasResult = useMemo(() => !!result, [result]);
  const hasEmployeeId = useMemo(() => !!formValues.employeeId, [formValues.employeeId]);
  const canSubmit = useMemo(
    () => hasEmployeeId && formValues.role && formValues.location && formValues.department,
    [hasEmployeeId, formValues.role, formValues.location, formValues.department],
  );

  useEffect(() => {
    if (hasAppliedDefaultLocation.current) {
      return;
    }

    if (!defaultLocationName) {
      return;
    }

    setFormValues((prev) => {
      if (prev.location) {
        return prev;
      }
      return {
        ...prev,
        location: defaultLocationName,
      };
    });

    hasAppliedDefaultLocation.current = true;
  }, [defaultLocationName]);

  useEffect(() => {
    if (!formValues.location) {
      return;
    }

    const stillExists = branchOptions.some((option) => option.value === formValues.location);
    if (!stillExists) {
      setFormValues((prev) => ({
        ...prev,
        location: '',
      }));
    }
  }, [branchOptions, formValues.location]);

  useEffect(() => {
    let isActive = true;

    const loadEmployeeId = async () => {
      try {
        const response = await generateEmployeeId();
        const nextEmployeeId = response?.employeeId;
        if (nextEmployeeId && isActive) {
          setFormValues((prev) => ({
            ...prev,
            employeeId: nextEmployeeId,
          }));
          setErrors((prev) => {
            if (!prev?.employeeId) {
              return prev;
            }
            const next = { ...prev };
            delete next.employeeId;
            return next;
          });
        }
      } catch (error) {
        if (!isActive) {
          return;
        }
        const message =
          error?.response?.data?.message ||
          (Array.isArray(error?.response?.data?.message)
            ? error.response.data.message.join('. ')
            : error?.message) ||
          t(
            'employeeDirectory:inviteModal.messages.generateIdError',
            'Unable to generate a unique employee ID. Please try reopening this form.',
          );
        setErrors((prev) => ({
          ...prev,
          general: prev?.general ?? message,
        }));
      }
    };

    loadEmployeeId();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    setIsOpening(true);
    const timeoutId = window.setTimeout(() => setIsOpening(false), 300);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (formValues.employeeId) {
      setIsOpening(false);
    }
  }, [formValues.employeeId]);

  const handleChange = (field) => (event) => {
    const value = event?.target?.value ?? '';
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = () => {
    const validation = {};
    if (!formValues.email?.trim()) {
      validation.email = t(
        'employeeDirectory:inviteModal.validation.emailRequired',
        'Email is required.',
      );
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email)) {
      validation.email = t(
        'employeeDirectory:inviteModal.validation.emailInvalid',
        'Enter a valid email address.',
      );
    }
    if (!formValues.employeeId?.trim()) {
      validation.employeeId = t(
        'employeeDirectory:inviteModal.validation.employeeIdRequired',
        'Employee ID is required.',
      );
    }
    if (!formValues.role?.trim()) {
      validation.role = t(
        'employeeDirectory:inviteModal.validation.roleRequired',
        'Role is required.',
      );
    } else if (!formValues.department?.trim()) {
      validation.role = t(
        'employeeDirectory:inviteModal.validation.roleUnsupported',
        'Selected role is not supported.',
      );
    }
    if (!formValues.location?.trim()) {
      validation.location = t(
        'employeeDirectory:inviteModal.validation.branchRequired',
        'Branch is required.',
      );
    }
    return validation;
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    if (hasResult) {
      onClose?.();
      return;
    }

    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation)?.length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const invitation = await inviteEmployee({
        email: formValues.email?.trim(),
        invitedName: formValues.invitedName?.trim() || undefined,
        employeeId: formValues.employeeId?.trim(),
        department: formValues.department?.trim(),
        role: formValues.role?.trim(),
        location: formValues.location?.trim(),
        startDate: formValues.startDate ? new Date(formValues.startDate).toISOString() : undefined,
        message: formValues.message?.trim() || undefined,
      });

      setResult({ invitation, email: formValues.email?.trim() });
      onInvitationCreated?.(invitation);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
          (Array.isArray(error?.response?.data?.message)
            ? error.response.data.message.join('. ')
            : error?.message) ||
        t(
          'employeeDirectory:inviteModal.messages.submitError',
          'Unable to send invitation.',
        );
      setErrors({ general: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 w-full max-w-2xl h-[85vh] overflow-hidden flex flex-col relative">
        {isOpening && (
          <div className="absolute inset-0 z-10 bg-background/70 backdrop-blur-sm">
            <LoadingState
              message={t(
                'employeeDirectory:inviteModal.loading',
                'Preparing invitation form…',
              )}
              heightClassName="h-full"
              spinnerSize="h-10 w-10"
            />
          </div>
        )}
        <div className="flex items-center justify-between p-6 border-b border-border shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {t('employeeDirectory:inviteModal.title', 'Invite Employee')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t(
                'employeeDirectory:inviteModal.subtitle',
                'Send a secure onboarding link for your new hire to complete their account setup.',
              )}
            </p>
          </div>
          <Button variant="ghost" onClick={onClose} iconName="X" iconPosition="right" />
        </div>

        <div className={`flex-1 flex flex-col p-6 min-h-0 transition-opacity duration-200 ${isOpening ? 'pointer-events-none select-none opacity-0' : 'opacity-100'}`}>
          {errors?.general && (
            <div className="mb-4 shrink-0 bg-error/10 border border-error/20 text-error text-sm rounded-md p-4 flex items-start space-x-3">
              <Icon name="AlertTriangle" size={18} className="mt-0.5" />
              <span>{errors.general}</span>
            </div>
          )}

          {hasResult ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4 py-12">
              <div className="w-16 h-16 rounded-full bg-success/10 text-success flex items-center justify-center mx-auto">
                <Icon name="CheckCircle" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-foreground">
                {t('employeeDirectory:inviteModal.result.title', 'Invitation Sent')}
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                {t(
                  'employeeDirectory:inviteModal.result.description',
                  "We've emailed {{email}} with onboarding instructions. They'll appear in your directory after completing their registration.",
                  { email: result?.email },
                )}
              </p>
              <Button variant="default" onClick={onClose} fullWidth>
                {t('employeeDirectory:inviteModal.actions.close', 'Close')}
              </Button>
            </div>
          ) : (
            <form className="flex-1 flex flex-col" onSubmit={handleSubmit}>
              <div className="flex-1 overflow-y-auto pr-2">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label={t('employeeDirectory:inviteModal.fields.email', 'Employee Email')}
                      type="email"
                      value={formValues.email}
                      onChange={handleChange('email')}
                      required
                      error={errors?.email}
                    />
                    <Input
                      label={t('employeeDirectory:inviteModal.fields.name', 'Employee Name')}
                      value={formValues.invitedName}
                      onChange={handleChange('invitedName')}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label={t('employeeDirectory:inviteModal.fields.employeeId', 'Employee ID')}
                      value={formValues.employeeId}
                      readOnly
                      className="bg-muted text-muted-foreground"
                      description={t(
                        'employeeDirectory:inviteModal.fields.employeeIdDescription',
                        'Generated automatically when the form opens.',
                      )}
                      required
                      error={errors?.employeeId}
                    />
                    <Select
                      label={t('employeeDirectory:inviteModal.fields.role', 'Role')}
                      options={roleOptions}
                      value={formValues.role}
                      onChange={(value) => {
                        const selectedRole = roleOptions.find((option) => option.value === value);
                        setFormValues((prev) => ({
                          ...prev,
                          role: value,
                          department: selectedRole?.department ?? '',
                        }));
                        setErrors((prev) => {
                          if (!prev?.role && !prev?.department) {
                            return prev;
                          }
                          const next = { ...prev };
                          delete next.role;
                          delete next.department;
                          return next;
                        });
                      }}
                      placeholder={t(
                        'employeeDirectory:inviteModal.fields.rolePlaceholder',
                        'Select a role',
                      )}
                      required
                      error={errors?.role}
                      searchable
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Select
                        label={t('employeeDirectory:inviteModal.fields.branch', 'Branch')}
                        options={branchOptions}
                        value={formValues.location}
                        onChange={(value) => {
                          setFormValues((prev) => ({
                            ...prev,
                            location: value,
                          }));
                          setErrors((prev) => {
                            if (!prev?.location) {
                              return prev;
                            }
                            const next = { ...prev };
                            delete next.location;
                            return next;
                          });
                        }}
                        onOpenChange={(isOpen) => {
                          if (isOpen && !locationsLoading && branchOptions.length === 0) {
                            refetchLocations().catch(() => {});
                          }
                        }}
                        placeholder={
                          locationsLoading
                            ? t(
                                'employeeDirectory:inviteModal.fields.branchLoading',
                                'Loading branches…',
                              )
                            : t(
                                'employeeDirectory:inviteModal.fields.branchPlaceholder',
                                'Select a branch',
                              )
                        }
                        required
                        error={
                          errors?.location ?? (
                            locationsError && branchOptions.length === 0
                              ? t(
                                  'employeeDirectory:inviteModal.messages.branchLoadError',
                                  'Unable to load branches.',
                                )
                              : undefined
                          )
                        }
                        description={t(
                          'employeeDirectory:inviteModal.fields.branchDescription',
                          'Assign the employee to a branch.',
                        )}
                        loading={locationsLoading}
                        searchable
                      />
                      {!locationsLoading && locationsError && branchOptions.length === 0 && (
                        <p className="text-sm text-muted-foreground">
                          {t(
                            'employeeDirectory:inviteModal.messages.branchRetry',
                            'Unable to load branches. Please try again later.',
                          )}
                        </p>
                      )}
                    </div>
                    <DatePicker
                      label={t('employeeDirectory:inviteModal.fields.startDate', 'Start Date')}
                      value={formValues.startDate}
                      onChange={handleChange('startDate')}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {t('employeeDirectory:inviteModal.fields.message', 'Personal Message (optional)')}
                    </label>
                    <textarea
                      className="min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      value={formValues.message}
                      onChange={handleChange('message')}
                      placeholder={t(
                        'employeeDirectory:inviteModal.fields.messagePlaceholder',
                        'Share a welcome note or important instructions for their first day.',
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-dashed border-border mt-4 shrink-0">
                <Button variant="outline" onClick={onClose} type="button">
                  {t('employeeDirectory:inviteModal.actions.cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  loading={isSubmitting}
                  disabled={!canSubmit}
                >
                  {t('employeeDirectory:inviteModal.actions.submit', 'Send Invitation')}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteEmployeeModal;
